import {
  Box,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  VStack,
  HStack,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Badge,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { MdAssignment, MdSecurity, MdHome, MdWarning, MdAttachMoney, MdTrendingUp } from 'react-icons/md';
import type { Dictamen } from './types';
import PieChart from '../../components/charts/PieChart';
import BarChart from '../../components/charts/BarChart';
import { useEstadisticasDictamenes } from '../../hooks/useEstadisticasDictamenes';
import EstadisticasOperadores from './EstadisticasOperadores';
import AlertasDictamenes from './AlertasDictamenes';

interface EstadisticasDictamenesProps {
  dictamenes: Dictamen[];
}

export default function EstadisticasDictamenes({ dictamenes }: EstadisticasDictamenesProps) {
  const estadisticas = useEstadisticasDictamenes(dictamenes);
  
  // Calcular tipos directamente
  const tiposDirectos = dictamenes.reduce((acc, d) => {
    const tipo = d.tipo || 'Sin tipo';
    acc[tipo] = (acc[tipo] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Configuración del gráfico de pastel
  const pieChartData = Object.values(estadisticas.porTipo);
  const pieChartOptions = {
    labels: Object.keys(estadisticas.porTipo),
    colors: ['#5b182b', '#bb8c52', '#161a1d', '#644c2eff'],
    legend: { 
      position: 'bottom' as const,
      fontSize: '10px'
    },
    dataLabels: { 
      enabled: true,
      style: {
        fontSize: '10px',
        fontWeight: 'bold'
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  // Configuración del gráfico de barras por días de la semana actual
  const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  
  // Obtener el rango de la semana actual
  const hoy = new Date();
  const inicioSemana = new Date(hoy);
  inicioSemana.setDate(hoy.getDate() - (hoy.getDay() === 0 ? 6 : hoy.getDay() - 1));
  inicioSemana.setHours(0, 0, 0, 0);
  
  const finSemana = new Date(inicioSemana);
  finSemana.setDate(inicioSemana.getDate() + 6);
  finSemana.setHours(23, 59, 59, 999);
  
  const porDiaSemana = diasSemana.map(dia => {
    const count = dictamenes.filter(d => {
      if (!d.fechaSolicitud) return false;
      const fecha = new Date(d.fechaSolicitud);
      
      // Solo contar si está en la semana actual
      if (fecha < inicioSemana || fecha > finSemana) return false;
      
      const diaSemanaNum = fecha.getDay();
      const diaTexto = diasSemana[diaSemanaNum === 0 ? 6 : diaSemanaNum - 1];
      return diaTexto === dia;
    }).length;
    return count;
  });

  const barChartData = [{
    name: 'Dictámenes',
    data: porDiaSemana
  }];
  const barChartOptions = {
    chart: { 
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
      }
    },
    xaxis: { 
      categories: diasSemana
    },
    colors: ['#bb8c52'],
    dataLabels: { 
      enabled: false
    },
    grid: { 
      show: false
    }
  };

  return (
    <Box mb={2}>
      {/* Alertas flotantes */}
      <AlertasDictamenes dictamenes={dictamenes} />
      
      {/* Tarjetas de estadísticas principales */}
      <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={2}>
        <Card bg="gradient(135deg, #5b182b 0%, #691f30 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdAssignment} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color="black">{estadisticas.total}</Text>
                <Text fontSize="sm" color="black">Total Dictámenes</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gradient(135deg, #681a2d 0%, #5b182b 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdSecurity} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color="black">{tiposDirectos['Dictamen de Seguridad y Prevención'] || 0}</Text>
                <Text fontSize="sm" color="black">Seguridad y Prevención</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gradient(135deg, #691f30 0%, #681a2d 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdWarning} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color="black">{tiposDirectos['Dictamen de Riesgo'] || 0}</Text>
                <Text fontSize="sm" color="black">Riesgo</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gradient(135deg, #bb8c52 0%, #a67c47 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdHome} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color="black">{tiposDirectos['Dictamen de Habitabilidad'] || 0}</Text>
                <Text fontSize="sm" color="black">Habitabilidad</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gradient(135deg, #2d5a27 0%, #3d7c47 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdAttachMoney} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color="black">${estadisticas.ingresosPagados.toFixed(0)}</Text>
                <Text fontSize="sm" color="black">Ingresos</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gradient(135deg, #1a365d 0%, #2c5282 100%)">
          <CardBody>
            <HStack>
              <Icon as={MdTrendingUp} boxSize={8} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color="black">{estadisticas.porcentajePagado.toFixed(1)}%</Text>
                <Text fontSize="sm" color="black">% Pagados</Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Gráficos */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold" color="#5b182b">
                Distribución por Tipo
              </Text>
              <Box h="450px" w="100%" display="flex" alignItems="center" justifyContent="center">
                {pieChartData.length > 0 ? (
                  <PieChart 
                    chartData={pieChartData}
                    chartOptions={pieChartOptions}
                  />
                ) : (
                  <Text color="gray.500">No hay datos para mostrar</Text>
                )}
              </Box>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold" color="#5b182b">
                Dictámenes - Semana Actual
              </Text>
              <Box h="350px" w="100%">
                <BarChart 
                  chartData={barChartData}
                  chartOptions={barChartOptions}
                />
              </Box>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Esta semana: {porDiaSemana.reduce((a, b) => a + b, 0)} dictámenes
              </Text>
            </VStack>
          </CardBody>
        </Card>

        <EstadisticasOperadores dictamenes={dictamenes} />
      </SimpleGrid>
    </Box>
  );
}